import classNames from 'classnames';
import React, { useContext, useRef, useState } from 'react';
import { DragType } from '../../../models/dragTypes';
import MixcloudUser from '../../../models/mixcloud/mixcloudUser';
import PlaylistColumns, {
  ColumnKey,
  ColumnScope,
  TrackProperty,
} from '../../../services/columnSettings/playlistColumns';
import { Context } from '../../../services/_globalContext/context';
import { preventDefaultStopPropogation } from '../../../utilities/preventDefault';
import FadilaMenuItem from '../../common/contextMenu/fadilaMenuItem';
import useColumnSettings from '../../common/hooks/useColumnSettings';
import s from '../../common/itemRow/itemRow.module.scss';
import ItemRowWrapper from '../../common/itemRow/ItemRowWrapper';
import sS from '../../common/streamingServiceCommon/streamSvcSearchResultRow.module.scss';
import mc from '../mixcloud.module.scss';
import { openOnMixcloud } from '../openOnMixcloud';

export interface MixcloudUserRowProps {
  /** Item array index */
  index: number;
  item: MixcloudUser;
  /** Show what this account has uploaded in the middle list. */
  openUser(user: MixcloudUser): void;

  isSelected: boolean;
  selectedItemsRef: React.MutableRefObject<Iterable<MixcloudUser>>;

  handleToggleItem(plItm: MixcloudUser): void;
  changeItemSelectionState(
    plItm: MixcloudUser,
    selectState: boolean,
    removePrevious?: boolean,
  ): void;
  handleShiftSelect(plItm: MixcloudUser): void;
  clearSelected(): void;

  portalTarget?: HTMLElement;
}

/**
 * One account in the Mixcloud search results - a DJ, a station, a radio
 * show. Opening it lists what they have uploaded, which is how you get from
 * a name you know to the sets themselves; Mixcloud has no playlists to
 * search, so this is the second half of its search rather than an extra.
 *
 * Nothing here is draggable: an account is not a list of tracks Fadila can
 * expand without fetching, unlike a SoundCloud playlist, so it is opened
 * rather than dropped.
 */
export default function UserSearchResult({
  index,
  item,
  openUser,

  portalTarget,
  isSelected,
  selectedItemsRef,
  changeItemSelectionState,
  handleShiftSelect,
  handleToggleItem,
  clearSelected,
}: MixcloudUserRowProps) {
  const ctx = useContext(Context);

  const titleElRef = useRef<HTMLTableDataCellElement>(null);

  const [isDragging, setIsDragging] = useState(false);

  const { columns, columnSettings } = useColumnSettings(ColumnScope.MixcloudUserSearch);

  const onDragStart = (ev: React.DragEvent) => {
    setIsDragging(true);

    // Only the name travels. Nothing in the app accepts an account as a
    // drop, and a drag that carries text/plain still does something sane
    // when it lands outside - see the class comment.
    ev.dataTransfer.setData('dragType', DragType.None);
    ev.dataTransfer.setData('text/plain', item.url);
    ev.dataTransfer.setData('name', item.name);

    ev.dataTransfer.effectAllowed = 'copy';

    if (titleElRef.current) {
      ev.dataTransfer.setDragImage(titleElRef.current, 0, 0);
    }
    if (!isSelected) {
      clearSelected();
      changeItemSelectionState(item, true, true);
    }
  };
  function onDragEnd(_ev: React.DragEvent) {
    setIsDragging(false);

    delete ctx.dndData[item.key];
  }

  const avatar = item.pictures && (item.pictures.medium || item.pictures.thumbnail);

  return (
    <ItemRowWrapper<MixcloudUser>
      index={index}
      item={item}
      itemId={item.key}
      className={sS.row}

      onDragStart={onDragStart}
      onDragEnd={onDragEnd}

      isSelected={isSelected}
      isDragging={isDragging}

      handleShiftSelect={() => handleShiftSelect(item)}
      handleToggleItem={() => handleToggleItem(item)}
      selectOnlyItem={() => changeItemSelectionState(item, true, true)}

      handlePlay={() => openUser(item)}

      menuItems={
        <>
          <FadilaMenuItem
            onClick={preventDefaultStopPropogation(() => openUser(item))}
            attributes={{
              className: 'secondary',
            }}
          >
            Show uploads
          </FadilaMenuItem>
          <FadilaMenuItem onClick={preventDefaultStopPropogation(() => openOnMixcloud(item.url))}>
            Open on Mixcloud
          </FadilaMenuItem>
        </>
      }
      portalTarget={portalTarget}
    >
      {columnSettings && (
        <>
          <div className={classNames(s.rowCell, s.playBtn)}>
            <button
              className="play-button"
              onClick={preventDefaultStopPropogation(() => openUser(item))}
            >
              <span role="img" aria-label="Uploads">
                🔗
              </span>
            </button>
          </div>

          {columns.map((col) => {
            const colKey = col as ColumnKey;

            switch (colKey) {
              case TrackProperty.title:
                return (
                  <div
                    key={colKey}
                    ref={titleElRef}
                    // As on the header cells and the show rows - it is what
                    // the browser tests select by (e2e/README.md).
                    data-column-name={colKey}
                    className={classNames(s.rowCell, {
                      [s.rightAlignCell]: PlaylistColumns[colKey].alignRight,
                    })}
                    style={{
                      width: columnSettings[colKey]?.width,
                    }}
                    title={
                      item.cloudcast_count !== undefined
                        ? `${item.name} - ${item.cloudcast_count} shows`
                        : item.name
                    }
                  >
                    {item.name}
                    <span className={mc.badge}>{item.username}</span>
                  </div>
                );

              case 'image':
                return (
                  <div
                    key={colKey}
                    data-column-name={colKey}
                    className={classNames(s.rowCell, {
                      [s.rightAlignCell]: PlaylistColumns[colKey].alignRight,
                    })}
                    style={{
                      width: columnSettings[colKey]?.width,
                    }}
                  >
                    {avatar && <img className={mc.avatarLarge} src={avatar} alt={item.name} />}
                  </div>
                );

              default:
                throw new Error('Unexpected column mismatch!');
            }
          })}
        </>
      )}
    </ItemRowWrapper>
  );
}
